/**
 * ClassSelectorBar Component
 * 
 * A dropdown bar for selecting database classes, styled similar to PreviewUrlBar.
 * Shows class name and row count with a custom dropdown menu.
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { ParseClass } from '../../types/AppPanel';

interface ClassSelectorBarProps {
  /** Available classes */
  classes?: ParseClass[];
  /** Currently selected class name */
  selectedClass?: string | null;
  /** Callback when class changes */
  onClassChange?: (className: string) => void;
  /** URL to open dashboard */
  dashboardUrl?: string;
}

export default function ClassSelectorBar({
  classes,
  selectedClass,
  onClassChange,
  dashboardUrl,
}: ClassSelectorBarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Get selected class info
  const selectedClassInfo = useMemo(() => {
    if (!classes || !selectedClass) return null;
    return classes.find(c => c.name === selectedClass);
  }, [classes, selectedClass]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Handle class selection
  const handleSelectClass = (className: string) => {
    setIsOpen(false);
    onClassChange?.(className);
  };

  // No classes available
  if (!classes || classes.length === 0) {
    return (
      <div className="relative flex-shrink min-w-[80px] max-w-[320px]">
        <div className="
          flex items-center gap-2 w-full
          bg-white/5 border border-white/15 rounded-lg
          px-2 sm:px-3 py-2
        ">
          <span className="text-sm text-white/40 truncate">No classes</span>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative flex-shrink min-w-[80px] max-w-[320px]">
      {/* Selector Bar */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="
          flex items-center gap-1 sm:gap-2 w-full
          bg-white/5 border border-white/15 rounded-lg
          px-2 sm:px-3 py-2
          hover:bg-white/8 hover:border-white/20
          focus:outline-none focus:border-white/20 focus:ring-1 focus:ring-[#208AEC]/30
          transition-all duration-200
          text-left
        "
      >
        {/* Selected Class Name - responsive truncation */}
        <span className="flex-1 text-sm text-white font-medium truncate min-w-0">
          {selectedClass || 'Select a class'}
        </span>

        {/* Row count badge - hidden on small screens */}
        {selectedClassInfo && (
          <span className="hidden md:inline text-xs text-white/50 bg-white/10 px-2 py-0.5 rounded-full flex-shrink-0">
            {selectedClassInfo.rowCount}
          </span>
        )}

        {/* Open in Dashboard button - hidden on small screens */}
        {dashboardUrl && selectedClass && (
          <a
            href={`${dashboardUrl}/browser/${selectedClass}`}
            target="_blank"
            rel="noreferrer noopener"
            onClick={(e) => e.stopPropagation()}
            className="hidden lg:block p-1 rounded hover:bg-white/10 text-white/40 hover:text-white/70 transition-colors flex-shrink-0"
            title="Open in Dashboard"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M18 13V19C18 20.1046 17.1046 21 16 21H5C3.89543 21 3 20.1046 3 19V8C3 6.89543 3.89543 6 5 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M15 3H21V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
        )}

        {/* Dropdown arrow */}
        <svg 
          className={`text-white/40 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          width="14" height="14" viewBox="0 0 16 16" fill="none"
        >
          <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Classes Dropdown */}
      {isOpen && (
        <div className="
          absolute top-full left-0 right-0 mt-1 z-[9999]
          bg-[#0c121c] backdrop-blur-xl
          border border-white/10 rounded-lg
          shadow-xl shadow-black/50
          max-h-[280px] overflow-y-auto
          py-1
        ">
          {classes.map((cls) => {
            const isActive = cls.name === selectedClass;
            const isSystemClass = cls.name.startsWith('_');
            return (
              <button
                key={cls.name}
                type="button"
                onClick={() => handleSelectClass(cls.name)}
                className={`
                  w-full flex items-center justify-between gap-3
                  px-3 py-2.5 text-left
                  transition-colors duration-100
                  ${isActive
                    ? 'bg-[#208AEC]/15 text-white'
                    : 'text-white/80 hover:bg-white/5 hover:text-white'
                  }
                `}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {/* Small dot indicator for system classes */}
                  {isSystemClass && (
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500/80 flex-shrink-0" />
                  )}
                  <span className={`text-sm font-medium truncate ${isSystemClass ? 'text-white/70' : ''}`}>
                    {cls.name}
                  </span>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Row count */}
                  <span className="text-xs text-white/40 tabular-nums">
                    {cls.rowCount}
                  </span>
                  
                  {/* Checkmark for selected */}
                  {isActive && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-[#208AEC]">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

